import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { fmtDate } from "@/lib/format";

const f0 = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 });
const f2 = new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const R = (v) => `Rs ${f0.format(Math.round(v || 0))}`;
const R2 = (v) => `Rs ${f2.format(v || 0)}`;
const Q = (v) => (v ? (Number.isInteger(v) ? String(v) : v.toFixed(1)) : "-");
const P = (v) => `${((v || 0) * 100).toFixed(1)}%`;
const AMBER = [217, 119, 6], DARK = [30, 41, 59];

const head = { fillColor: DARK, textColor: 255, fontStyle: "bold", fontSize: 8 };
const foot = { fillColor: [254, 243, 199], textColor: [120, 53, 15], fontStyle: "bold", fontSize: 8 };

function title(doc, text, y) {
  doc.setFontSize(11); doc.setTextColor(...DARK); doc.setFont("helvetica", "bold");
  doc.text(text, 40, y);
  return y + 8;
}

function header(doc, r, label) {
  const w = doc.internal.pageSize.getWidth();
  doc.setFillColor(...AMBER); doc.rect(0, 0, w, 6, "F");
  doc.setFont("helvetica", "bold"); doc.setFontSize(16); doc.setTextColor(...DARK);
  doc.text(r.business_name || "MANJAP HOSPITALITY SOLUTIONS", 40, 38);
  doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(100);
  doc.text(`${label}  |  ${fmtDate(r.start, { day: "2-digit", month: "short", year: "numeric" })} to ${fmtDate(r.end, { day: "2-digit", month: "short", year: "numeric" })}`, 40, 54);
  doc.text(`Generated ${new Date().toLocaleString("en-IN")}`, w - 40, 54, { align: "right" });
}

function footer(doc) {
  const n = doc.internal.getNumberOfPages();
  for (let i = 1; i <= n; i++) {
    doc.setPage(i);
    const w = doc.internal.pageSize.getWidth(), h = doc.internal.pageSize.getHeight();
    doc.setFontSize(7); doc.setTextColor(140);
    doc.text("Manjap Sales & Expense CRM  -  Good Food. Great Mood.", 40, h - 18);
    doc.text(`Page ${i} of ${n}`, w - 40, h - 18, { align: "right" });
  }
}

const last = (doc) => doc.lastAutoTable.finalY;

export function exportReportPdf(r, label) {
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  const s = r.summary;
  header(doc, r, label);

  let y = title(doc, "Profit & Loss Summary", 80);
  autoTable(doc, {
    startY: y, theme: "grid", headStyles: head, styles: { fontSize: 9, cellPadding: 5 }, margin: { left: 40, right: 40 },
    tableWidth: 360, columnStyles: { 1: { halign: "right" } },
    head: [["Particulars", "Amount"]],
    body: [
      ["Day shift sales", R2(s.day_sales)], ["Night shift sales", R2(s.night_sales)],
      ["GROSS SALES", R2(s.gross)], [`Less: Deduction @ ${s.deduction_pct}%`, `- ${R2(s.deduction)}`],
      [`NET SALES (${100 - s.deduction_pct}%)`, R2(s.net)], ["Less: Variable / daily expenses", `- ${R2(s.variable_expense)}`],
      ["Less: Fixed costs (pro-rated)", `- ${R2(s.fixed_cost)}`], ["Total expenses", R2(s.total_expense)],
      ["NET PROFIT / (LOSS)", R2(s.profit)], ["Profit margin on gross", P(s.margin)],
      ["Total plates served", Q(s.plates)], ["Trading days", String(s.trading_days)], ["Avg sales / trading day", R2(s.avg_per_day)],
    ],
    didParseCell: (d) => { if (d.section === "body" && [2, 4, 8].includes(d.row.index)) { d.cell.styles.fontStyle = "bold"; d.cell.styles.fillColor = [254, 243, 199]; } },
  });
  const sumEnd = last(doc);
  autoTable(doc, {
    startY: y, theme: "grid", headStyles: head, styles: { fontSize: 8, cellPadding: 4 }, margin: { left: 430, right: 40 },
    columnStyles: { 1: { halign: "right" }, 2: { halign: "right" } },
    head: [["Week", "Gross", "Var. Exp"]],
    body: r.weeks.map((w) => [`${fmtDate(w.week_start)} - ${fmtDate(w.week_end)}`, R(w.gross), R(w.variable_expense)]),
  });
  y = Math.max(sumEnd, last(doc));

  doc.addPage(); header(doc, r, label);
  y = title(doc, "Day-wise Sales & Expenses", 80);
  const days = r.days;
  autoTable(doc, {
    startY: y, theme: "striped", headStyles: head, footStyles: foot, styles: { fontSize: 7.5, cellPadding: 3 }, margin: { left: 40, right: 40 },
    columnStyles: Object.fromEntries([1, 2, 3, 4, 5, 6, 7, 8].map((i) => [i, { halign: "right" }])),
    head: [["Date", "Plates", "Day Sales", "Night Sales", "Gross", `Deduction ${s.deduction_pct}%`, "Net Sales", "Var. Expense", "Net - Var."]],
    body: days.map((d) => [fmtDate(d.date, { weekday: "short", day: "2-digit", month: "short" }), Q(d.plates), R(d.day_sales), R(d.night_sales), R(d.gross), R(d.deduction), R(d.net), R(d.variable_expense), R(d.net_after_variable)]),
    foot: [["TOTAL", Q(s.plates), R(s.day_sales), R(s.night_sales), R(s.gross), R(s.deduction), R(s.net), R(s.variable_expense), R(s.net - s.variable_expense)]],
  });

  doc.addPage(); header(doc, r, label);
  y = title(doc, "Item-wise Sales", 80);
  autoTable(doc, {
    startY: y, theme: "striped", headStyles: head, footStyles: foot, styles: { fontSize: 8, cellPadding: 3 }, margin: { left: 40, right: 40 },
    columnStyles: Object.fromEntries([2, 3, 4, 5, 6, 7].map((i) => [i, { halign: "right" }])),
    head: [["Category", "Item", "Rate", "Day Qty", "Night Qty", "Total Qty", "Sales", "% of Sales"]],
    body: r.items.map((i) => [i.category, i.name, R(i.rate), Q(i.day_qty), Q(i.night_qty), Q(i.qty), R(i.sales), P(i.pct)]),
    foot: [["", "TOTAL", "", Q(r.items.reduce((a, i) => a + i.day_qty, 0)), Q(r.items.reduce((a, i) => a + i.night_qty, 0)), Q(s.plates), R(s.gross), "100%"]],
  });

  doc.addPage(); header(doc, r, label);
  y = title(doc, "Item x Date Sheet (plates, day + night)", 80);
  const dates = days.map((d) => d.date);
  const pageW = doc.internal.pageSize.getWidth() - 80;
  const fs = dates.length > 20 ? 5.5 : 7;
  autoTable(doc, {
    startY: y, theme: "grid", headStyles: { ...head, fontSize: fs, halign: "center" }, footStyles: { ...foot, fontSize: fs }, margin: { left: 40, right: 40 },
    styles: { fontSize: fs, cellPadding: 1.5, halign: "center" }, tableWidth: pageW,
    columnStyles: { 0: { halign: "left", cellWidth: 110 } },
    head: [["Item", ...dates.map((d) => d.slice(8)), "Qty", "Sales"]],
    body: r.items.map((i) => [i.name, ...dates.map((d) => Q(i.by_date[d])), Q(i.qty), f0.format(i.sales)]),
    foot: [["TOTAL", ...days.map((d) => Q(d.plates)), Q(s.plates), f0.format(s.gross)]],
  });

  doc.addPage(); header(doc, r, label);
  y = title(doc, "Expense Head-wise", 80);
  const vars = r.expenses.filter((e) => e.kind === "variable"), fixed = r.expenses.filter((e) => e.kind === "fixed");
  autoTable(doc, {
    startY: y, theme: "striped", headStyles: head, footStyles: foot, styles: { fontSize: 8.5, cellPadding: 4 }, margin: { left: 40, right: 40 },
    columnStyles: { 2: { halign: "right" }, 3: { halign: "right" } },
    head: [["Type", "Expense Head", "Amount", "% of Expense"]],
    body: [...vars, ...fixed].map((e) => [e.kind === "fixed" ? "Fixed (pro-rated)" : e.category, e.name, R2(e.amount), P(s.total_expense ? e.amount / s.total_expense : 0)]),
    foot: [["", "TOTAL EXPENSE", R2(s.total_expense), "100%"]],
  });
  const pb = r.paid_by || [], pName = (p) => (p.name === "Unassigned" ? "Not assigned" : `${p.name} paid`);
  if (pb.length) {
    const pTot = pb.reduce((a, p) => a + p.amount, 0);
    const split = (p) => { let h1 = 0, h2 = 0; Object.entries(p.by_date).forEach(([d, v]) => (Number(d.slice(8)) <= 15 ? (h1 += v) : (h2 += v))); return [h1, h2]; };
    doc.addPage(); header(doc, r, label);
    y = title(doc, "Expenses Paid By - 1st-15th vs 16th-month end", 80);
    autoTable(doc, {
      startY: y, theme: "grid", headStyles: head, footStyles: foot, styles: { fontSize: 9, cellPadding: 5 }, margin: { left: 40, right: 40 }, tableWidth: 420,
      columnStyles: { 1: { halign: "right" }, 2: { halign: "right" }, 3: { halign: "right" } },
      head: [["Paid by", "1st - 15th", "16th - end", "Total"]],
      body: pb.map((p) => { const [a, b] = split(p); return [pName(p), R(a), R(b), R(p.amount)]; }),
      foot: [["TOTAL", R(pb.reduce((a, p) => a + split(p)[0], 0)), R(pb.reduce((a, p) => a + split(p)[1], 0)), R(pTot)]],
    });
    y = title(doc, "Date-wise - who paid what", last(doc) + 24);
    autoTable(doc, {
      startY: y, theme: "striped", headStyles: head, footStyles: foot, styles: { fontSize: 7.5, cellPadding: 3 }, margin: { left: 40, right: 40 },
      columnStyles: Object.fromEntries(pb.map((_, i) => [i + 1, { halign: "right" }]).concat([[pb.length + 1, { halign: "right" }]])),
      head: [["Date", ...pb.map(pName), "Total"]],
      body: r.days.map((d) => [fmtDate(d.date, { weekday: "short", day: "2-digit", month: "short" }), ...pb.map((p) => (p.by_date[d.date] ? R(p.by_date[d.date]) : "-")), R(pb.reduce((a, p) => a + (p.by_date[d.date] || 0), 0))]),
      foot: [["TOTAL", ...pb.map((p) => R(p.amount)), R(pTot)]],
    });
    doc.addPage(); header(doc, r, label);
    y = title(doc, "Head-wise - who paid for what", 80);
    autoTable(doc, {
      startY: y, theme: "striped", headStyles: head, footStyles: foot, styles: { fontSize: 8.5, cellPadding: 4 }, margin: { left: 40, right: 40 },
      columnStyles: Object.fromEntries(pb.map((_, i) => [i + 2, { halign: "right" }]).concat([[pb.length + 2, { halign: "right" }]])),
      head: [["Category", "Expense Head", ...pb.map(pName), "Total"]],
      body: vars.filter((e) => e.amount).map((e) => [e.category, e.name, ...pb.map((p) => (p.by_head[e.id] ? R(p.by_head[e.id]) : "-")), R(e.amount)]),
      foot: [["", "TOTAL", ...pb.map((p) => R(p.amount)), R(pTot)]],
    });
  }
  y = last(doc) + 50;
  doc.setFontSize(9); doc.setTextColor(80); doc.setFont("helvetica", "normal");
  doc.text("Prepared by: ____________________", 40, y);
  doc.text("Checked by: ____________________", 320, y);
  footer(doc);
  doc.save(`Manjap_Report_${r.start}_to_${r.end}.pdf`);
}
